import { Text, Spinner, Badge, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchProjects } from '../api/modules.api';
import {
  fetchCapitalSummary, fetchDrawdowns, fetchFundRequests, fetchInvestmentMetrics,
  type DrawdownStatus, type FundRequestStatus,
} from '../api/capital.api';
import { KpiCard } from '../components/shared/KpiCard';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoStore } from '../store/rancho.store';
import { PageTitle } from '../components/shared/PageTitle';

/**
 * F7 — "Inversión y Fondos" del portal inversionista. Reusa la API real de
 * F4 (`capital.handler.ts`, PR #102): fuentes de capital, ministraciones,
 * solicitudes de fondos y las cifras derivadas (ROI/TIR/cashflow) — todo
 * en modo solo-lectura, sin botones de aprobación (eso es back-office, no
 * el portal del inversionista).
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td: { padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
});

const DRAWDOWN_COLOR: Record<DrawdownStatus, 'success' | 'warning' | 'subtle'> = {
  PLANNED:   'warning',
  RECEIVED:  'success',
  CANCELLED: 'subtle',
};

const FUND_REQUEST_COLOR: Record<FundRequestStatus, 'success' | 'warning' | 'danger' | 'subtle'> = {
  PENDING:   'warning',
  APPROVED:  'success',
  REJECTED:  'danger',
  CANCELLED: 'subtle',
};

export function InvestorFundingPage() {
  const s = useStyles();
  const t = useT();
  const { formatCurrency } = useLocaleSettings();

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: projects } = useQuery({ queryKey: ['investor-projects', activeRanchoId], queryFn: () => fetchProjects(activeRanchoId) });
  const project = projects?.[0];
  const projectId = project?.projectId;

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['investor-funding-summary', projectId],
    queryFn: () => fetchCapitalSummary(projectId!),
    enabled: !!projectId,
  });
  const { data: drawdowns, isLoading: drawdownsLoading } = useQuery({
    queryKey: ['investor-funding-drawdowns', projectId],
    queryFn: () => fetchDrawdowns(projectId!),
    enabled: !!projectId,
  });
  const { data: fundRequests, isLoading: fundRequestsLoading } = useQuery({
    queryKey: ['investor-funding-requests', projectId],
    queryFn: () => fetchFundRequests(projectId!),
    enabled: !!projectId,
  });
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['investor-funding-metrics', projectId],
    queryFn: () => fetchInvestmentMetrics(projectId!),
    enabled: !!projectId,
  });

  // Cada monto se muestra en la moneda de su registro; los totales, en la del resumen (null con varias monedas).
  const fmt = (n: number, code?: string | null) => formatCurrency(n, code ?? summary?.currency);
  const fmtMoney = (n: number, code: string) => fmt(n, code);
  const fmtPct = (n: number | null) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

  return (
    <div className={s.root}>
      <PageTitle>{t.investorFunding.title}</PageTitle>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        {project ? `${project.name}${project.season ? ` — ${project.season}` : ''}` : t.investorFunding.subtitle}
      </Text>

      {(summaryLoading || metricsLoading) && <Spinner size="small" label={t.investorFunding.loading} />}

      {summary && metrics && (
        <div className={s.kpiRow}>
          <KpiCard label={t.investorFunding.kpiCommitted} value={summary.totalCommitted == null ? '—' : fmt(summary.totalCommitted)} />
          <KpiCard label={t.investorFunding.kpiDrawn} value={summary.totalDrawn == null ? '—' : fmt(summary.totalDrawn)} />
          <KpiCard label={t.investorFunding.kpiPending} value={summary.pendingToDraw == null ? '—' : fmt(summary.pendingToDraw)} />
          <KpiCard label={t.investorFunding.kpiCollected} value={metrics.totalCollected == null ? '—' : fmt(metrics.totalCollected)} />
          <KpiCard label={t.investorFunding.kpiRoi} value={fmtPct(metrics.roi)} />
          <KpiCard label={t.investorFunding.kpiIrr} value={fmtPct(metrics.annualIrr)} />
          <KpiCard
            label={t.investorFunding.kpiPayback}
            value={metrics.paybackMonths == null ? '—' : `${metrics.paybackMonths} ${t.investorFunding.months}`}
          />
        </div>
      )}

      {metrics?.byCurrency && Object.keys(metrics.byCurrency).length > 0 && (
        <div className={s.section} data-testid="metrics-by-currency">
          <span className={s.sectionTitle}>{t.investorFunding.byCurrencyTitle}</span>
          {Object.entries(metrics.byCurrency).map(([code, m]) => (
            <div key={code} data-testid={`metrics-currency-${code}`}>
              <Text weight="semibold">{code}</Text>
              <div className={s.kpiRow}>
                <KpiCard label={t.investorFunding.kpiDrawn} value={fmtMoney(m.totalInvested, code)} />
                <KpiCard label={t.investorFunding.kpiCollected} value={fmtMoney(m.totalCollected, code)} />
                <KpiCard label={t.investorFunding.kpiRoi} value={fmtPct(m.roi)} />
                <KpiCard label={t.investorFunding.kpiIrr} value={fmtPct(m.annualIrr)} />
                <KpiCard
                  label={t.investorFunding.kpiPayback}
                  value={m.paybackMonths == null ? '—' : `${m.paybackMonths} ${t.investorFunding.months}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorFunding.sourcesTitle}</span>
        {summary && summary.bySource.length > 0 && (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.investorFunding.tableName}</th>
                <th className={s.th}>{t.investorFunding.tableType}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.investorFunding.tableCommitted}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.investorFunding.tableDrawn}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.investorFunding.tablePending}</th>
              </tr>
            </thead>
            <tbody>
              {summary.bySource.map(src => (
                <tr key={src.sourceId}>
                  <td className={s.td}>{src.name}</td>
                  <td className={s.td}>{String(t.investorFunding[`sourceType${src.type}` as keyof typeof t.investorFunding] ?? src.type)}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmt(src.committed, src.currency)}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmt(src.drawn, src.currency)}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmt(src.pending, src.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {summary && summary.bySource.length === 0 && <Text size={200}>{t.investorFunding.noData}</Text>}
      </div>

      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorFunding.drawdownsTitle}</span>
        {drawdownsLoading && <Spinner size="small" label={t.investorFunding.loading} />}
        {!drawdownsLoading && (drawdowns ?? []).length === 0 && <Text size={200}>{t.investorFunding.noData}</Text>}
        {(drawdowns ?? []).length > 0 && (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.investorFunding.tableDate}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.investorFunding.tableAmount}</th>
                <th className={s.th}>{t.investorFunding.tableStatus}</th>
              </tr>
            </thead>
            <tbody>
              {drawdowns!.map(d => (
                <tr key={d.drawdownId}>
                  <td className={s.td}>{d.actualDate ?? d.plannedDate}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmt(d.actualAmount ?? d.plannedAmount, d.currency)}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={DRAWDOWN_COLOR[d.status]}>
                      {t.investorFunding[`drawdown${d.status}` as keyof typeof t.investorFunding]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorFunding.fundRequestsTitle}</span>
        {fundRequestsLoading && <Spinner size="small" label={t.investorFunding.loading} />}
        {!fundRequestsLoading && (fundRequests ?? []).length === 0 && <Text size={200}>{t.investorFunding.noData}</Text>}
        {(fundRequests ?? []).length > 0 && (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.investorFunding.tableCategory}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.investorFunding.tableAmount}</th>
                <th className={s.th}>{t.investorFunding.tableStatus}</th>
              </tr>
            </thead>
            <tbody>
              {fundRequests!.map(r => (
                <tr key={r.requestId}>
                  <td className={s.td}>{(t.investorFunding.fundCategories as Record<string, string>)[r.category] ?? r.category}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmt(r.amount, r.currency)}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={FUND_REQUEST_COLOR[r.status]}>
                      {t.investorFunding[`fundRequest${r.status}` as keyof typeof t.investorFunding]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
